import React, { Fragment, useState } from "react";
import { motion } from "framer-motion";
import date from "date-and-time";
import history from "../../utils/history";
import {
  Building2,
  Hash,
  DollarSign,
  X,
  Calendar,
  CreditCard,
  FileText,
  ChevronDown,
  ChevronUp,
  Clock,
  Receipt,
} from "lucide-react";

/**
 * Context API
 */
import useModal from "hooks/use-modal";

/**
 * Components
 */
import BaseButton from "components/base-button/base-button";
import API from "../../utils/api";

function formatMoney(number, decPlaces, decSep, thouSep) {
  decPlaces = isNaN((decPlaces = Math.abs(decPlaces))) ? 2 : decPlaces;
  decSep = typeof decSep === "undefined" ? "." : decSep;
  thouSep = typeof thouSep === "undefined" ? "," : thouSep;
  var sign = number < 0 ? "-" : "";
  var i = String(
    parseInt((number = Math.abs(Number(number) || 0).toFixed(decPlaces)))
  );
  var j = (j = i.length) > 3 ? j % 3 : 0;

  return (
    sign +
    (j ? i.substr(0, j) + thouSep : "") +
    i.substr(j).replace(/(\decSep{3})(?=\decSep)/g, "$1" + thouSep) +
    (decPlaces
      ? decSep +
        Math.abs(number - i)
          .toFixed(decPlaces)
          .slice(2)
      : "")
  );
}

function splitCamelCase(str) {
  return str.replace(/([a-z])([A-Z])/g, "$1 $2");
}

const ModalCreditorInfo = (props) => {
  const { openModal, modalTypes, closeModal } = useModal();
  const { account } = props.modalProps;
  const [files, setFiles] = React.useState([]);
  const [viewFiles, setViewFiles] = React.useState(false);
  const [expandBuckets, setExpandBuckets] = useState(false);
  const [paymentEligible, setPaymentEligible] = useState(false);
  const [loadingEligibility, setLoadingEligibility] = useState(true);
  const pattern = date.compile("MM/DD/YYYY");
  const dateOpened = new Date(account.creditor.last_payment_date);
  const dateOpenedAgency = new Date(account.agency.last_payment_date);

  // Animation variants
  const contentAnimation = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemAnimation = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.2 },
    },
  };

  const cardAnimation = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3 },
    },
  };

  // Info card component (collapsible + optional subtitle)
  const InfoCard = ({
    icon: Icon,
    title,
    subtitle,
    children,
    className = "",
    collapsible = true,
    defaultOpen = false,
    slim = false,
  }) => {
    const [open, setOpen] = React.useState(!collapsible ? true : defaultOpen);

    return (
      <motion.div
        variants={cardAnimation}
        className={`bg-gray-800/50 backdrop-blur rounded-xl border border-gray-700/50 ${className}`}
      >
        <button
          type="button"
          onClick={collapsible ? () => setOpen(!open) : undefined}
          className={`w-full ${slim ? "p-3 sm:p-4" : "p-4 sm:p-5"} flex items-center justify-between`}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`${slim ? "w-6 h-6" : "w-8 h-8"} bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0`}
            >
              <Icon
                className={`${slim ? "w-3.5 h-3.5" : "w-4 h-4"} text-blue-400`}
              />
            </div>
            <div className="text-left min-w-0">
              <h3
                className={`${slim ? "text-xs" : "text-sm"} font-semibold text-gray-300 uppercase tracking-wider whitespace-normal break-words`}
              >
                {title}
              </h3>
              {subtitle && (
                <p
                  className={`${slim ? "text-[11px]" : "text-xs"} text-gray-400 whitespace-normal break-words`}
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {collapsible && (
            <span className="ml-3 flex-shrink-0 text-gray-400">
              {open ? (
                <ChevronUp className="w-5 h-5" />
              ) : (
                <ChevronDown className="w-5 h-5" />
              )}
            </span>
          )}
        </button>

        {(!collapsible || open) && (
          <div
            className={`${slim ? "px-3 sm:px-4 pb-3 sm:pb-4 space-y-2" : "px-4 sm:px-5 pb-4 sm:pb-5 space-y-3"} border-t border-gray-700/40`}
          >
            {children}
          </div>
        )}
      </motion.div>
    );
  };

  // Detail row component
  const DetailRow = ({
    label,
    value,
    icon: Icon,
    highlight = false,
    compact = true,
  }) => (
    <div
      className={`flex items-center justify-between ${compact ? "py-0.5" : "py-1"}`}
    >
      <div className="flex items-center gap-2 text-gray-400">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className={`${compact ? "text-xs" : "text-sm"}`}>{label}</span>
      </div>
      <span
        className={`${highlight ? "text-white font-medium" : "text-gray-300"} ${compact ? "text-xs sm:text-sm" : "text-sm sm:text-base"}`}
      >
        {value}
      </span>
    </div>
  );

  // Check payment eligibility
  React.useEffect(() => {
    const checkEligibility = async () => {
      try {
        console.log(
          "[ModalCreditorInfo] Checking eligibility for account:",
          account.id,
          "type:",
          account.type
        );
        setLoadingEligibility(true);

        const response = await API.get(
          `/accounts/${account.id}/payment_plans/config`
        );

        if (response.data) {
          // Respect the backend's eligibility: the consumer can pay only when
          // the account is plan-, single-, or offer-eligible. (A resolved /
          // closed account returns all-false — e.g. a settled-in-full account
          // — and must NOT show "Make Payment".) The previous `|| true` here
          // defeated the whole check and showed the button unconditionally.
          const eligible = !!(
            response.data.eligible ||
            response.data.single_eligible ||
            response.data.offer_eligible
          );
          console.log("[ModalCreditorInfo] Payment eligibility:", eligible);
          setPaymentEligible(eligible);
        } else {
          // If no config, assume eligible if account is active/pending
          console.log(
            "[ModalCreditorInfo] No payment config, defaulting to eligible"
          );
          setPaymentEligible(true);
        }
      } catch (error) {
        console.log("[ModalCreditorInfo] Error fetching eligibility:", error);
        // On error, fallback to basic eligibility check
        setPaymentEligible(
          account.type === "active" || account.type === "pending"
        );
      } finally {
        setLoadingEligibility(false);
      }
    };

    if (account && account.id) {
      checkEligibility();
    }
  }, [account]);

  if (viewFiles) {
    return (
      <div className="flex flex-col h-full max-h-[100vh] sm:max-h-[90vh] p-0">
        <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 flex-shrink-0 sm:rounded-t-2xl">
          <button
            onClick={() => setViewFiles(false)}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors group z-10"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
          </button>

          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex items-center gap-3 sm:gap-4 pr-10"
          >
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate">
                Account Files
              </h2>
              <p className="text-blue-100 text-sm sm:text-base">
                {account.creditor.name}
              </p>
            </div>
          </motion.div>
        </div>

        <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1">
          <div className="space-y-3">
            {files.map((file, index) => (
              <motion.div
                key={file.file_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-800/50 backdrop-blur rounded-xl p-4 border border-gray-700/50 hover:border-gray-600 transition-colors cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-white mb-1">
                      {file.name}
                    </h4>
                    <p className="text-sm text-gray-400 mb-2">
                      {file.description}
                    </p>
                    <p className="text-xs text-gray-500">
                      Added {new Date(file.added).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-6">
            <BaseButton
              type="button"
              onClick={() => setViewFiles(false)}
              className="w-full py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base bg-gray-700 hover:bg-gray-600 text-white transition-all duration-200"
            >
              Close Files
            </BaseButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full max-h-[100vh] sm:max-h-[90vh] p-0">
      {/* Modal Header */}
      <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 px-4 sm:px-6 lg:px-8 py-4 sm:py-5 lg:py-6 flex-shrink-0 sm:rounded-t-2xl">
        <button
          onClick={closeModal}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors group z-10"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
        </button>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="flex items-center gap-3 sm:gap-4 pr-10"
        >
          <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center flex-shrink-0">
            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white leading-tight break-words whitespace-normal">
              {account.chargeoff_creditor
                ? account.chargeoff_creditor.name
                : account.creditor.name}
            </h2>
            <p className="text-blue-100 text-sm sm:text-base">
              Account Information
            </p>
          </div>
        </motion.div>
      </div>

      {/* Modal Body */}
      <motion.div
        className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1 min-h-0 overscroll-contain"
        variants={contentAnimation}
        initial="hidden"
        animate="visible"
      >
        {/* Horizontal balance bar (click to toggle breakdown) */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setExpandBuckets(!expandBuckets)}
            className={`w-full rounded-lg bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 p-3 sm:p-4 flex items-center justify-between hover:from-blue-600/30 hover:to-purple-600/30 transition-colors text-left ${expandBuckets ? "rounded-b-none border-b-0" : ""}`}
          >
            <div>
              <div className="text-[11px] sm:text-xs text-blue-200">
                Current Balance
              </div>
              <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">
                ${formatMoney(account.balance_current, 2, ".", ",")}
              </div>
              <div className="text-[11px] sm:text-xs text-blue-100/90">
                Original: ${formatMoney(account.balance_start, 2, ".", ",")}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {account.balance_buckets?.length > 0 && (
                <span className="text-white/80">
                  {expandBuckets ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </span>
              )}
            </div>
          </button>

          {account.balance_buckets?.length > 0 && expandBuckets && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              className="px-4 sm:px-5 pb-4 sm:pb-5 bg-gray-800/30 rounded-b-lg rounded-t-none border border-t-0 border-blue-500/30 -mt-px"
            >
              <div className="space-y-2 pt-2">
                {account.balance_buckets.map((bucket, index) => (
                  <DetailRow
                    key={index}
                    label={splitCamelCase(bucket.name)}
                    value={`$${formatMoney(bucket.balance_current, 2, ".", ",")}`}
                  />
                ))}
              </div>
            </motion.div>
          )}
        </div>

        {/* Consolidated details container */}
        <div className="space-y-4">
          <InfoCard
            icon={Building2}
            title="Information"
            defaultOpen
            collapsible={false}
            slim
            className="w-full"
          >
            <div className="divide-y divide-gray-700/50">
              {account.agency && (
                <div className="py-2">
                  <div className="text-[11px] uppercase text-gray-400 tracking-wider mb-1">
                    Agency
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="text-sm text-white truncate">
                      {account.agency.name}
                    </div>
                    <div className="text-xs text-gray-300 whitespace-nowrap">
                      Acct # {account.agency.ref_id}
                    </div>
                  </div>
                  {/* {account.agency.last_payment_amount !== undefined && (
                    <div className="text-[11px] text-gray-400 mt-1">
                      Last Payment: $
                      {formatMoney(
                        account.agency.last_payment_amount,
                        2,
                        ".",
                        ","
                      )}{" "}
                      on{" "}
                      {date
                        .format(dateOpenedAgency, pattern)
                        .toLocaleLowerCase()}
                    </div>
                  )} */}
                </div>
              )}

              {account.creditor && (
                <div className="py-2">
                  <div className="text-[11px] uppercase text-gray-400 tracking-wider mb-1">
                    Creditor
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="text-sm text-white truncate">
                      {account.creditor.name}
                    </div>
                    <div className="text-xs text-gray-300 whitespace-nowrap">
                      Acct # {account.creditor.ref_id}
                    </div>
                  </div>
                  {/* {account.creditor.last_payment_amount !== undefined &&
                    account.creditor.last_payment_amount !== 0 && (
                      <div className="text-[11px] text-gray-400 mt-1">
                        Last Payment: $
                        {formatMoney(
                          account.creditor.last_payment_amount,
                          2,
                          ".",
                          ","
                        )}{" "}
                        on{" "}
                        {date.format(dateOpened, pattern).toLocaleLowerCase()}
                      </div>
                    )} */}
                </div>
              )}

              {account.chargeoff_creditor && (
                <div className="py-2">
                  <div className="text-[11px] uppercase text-gray-400 tracking-wider mb-1">
                    Creditor at Charge Off
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="text-sm text-white truncate">
                      {account.chargeoff_creditor.name}
                    </div>
                    <div className="text-xs text-gray-300 whitespace-nowrap">
                      Acct # {account.chargeoff_creditor.ref_id}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </InfoCard>
        </div>
        {/* Balance breakdown now lives under the balance bar toggle above */}

        {/* Action Buttons (sticky footer inside scroll) */}
        <motion.div
          variants={itemAnimation}
          className="mt-6 sticky bottom-0 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 pt-4 pb-3 bg-gradient-to-t from-gray-900/95 to-transparent backdrop-blur"
        >
          {/* Debug info - remove after testing */}
          {console.log(
            "[ModalCreditorInfo] Render - loadingEligibility:",
            loadingEligibility,
            "paymentEligible:",
            paymentEligible
          )}

          <div className="space-y-3">
            {paymentEligible && (
              <BaseButton
                type="button"
                onClick={() => openModal(modalTypes.ModalOffer, { account })}
                className="w-full py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center gap-2"
              >
                <CreditCard className="w-4 h-4" />
                Make Payment
              </BaseButton>
            )}
          </div>

          {files.length > 0 && (
            <BaseButton
              type="button"
              onClick={() => setViewFiles(true)}
              className="w-full py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base bg-gray-700/50 hover:bg-gray-700 text-white transition-all duration-200 flex items-center justify-center gap-2 border border-gray-600"
            >
              <FileText className="w-4 h-4" />
              View Files ({files.length})
            </BaseButton>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ModalCreditorInfo;
